import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp,
  DollarSign,
  Package,
  ShoppingCart,
  RefreshCw,
  Rocket,
  Sparkles,
  ArrowRight,
  Target,
  Zap,
  Trophy,
  Plus,
  Search,
} from 'lucide-react';
import { api } from '../api/client';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { AnalyticsSkeleton } from '../components/ui/Skeleton';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function Analytics() {
  const queryClient = useQueryClient();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => api.analytics.dashboard(),
  });

  const { data: nichesData, isLoading: nichesLoading } = useQuery({
    queryKey: ['analytics-niches'],
    queryFn: () => api.analytics.niches(),
  });

  const isLoadingAll = isLoading || nichesLoading;

  const syncMutation = useMutation({
    mutationFn: () => api.analytics.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-niches'] });
    },
  });

  const hasData = (dashboard?.overall?.total_orders ?? 0) > 0;
  const hasListings = (dashboard?.overall?.pod_listings ?? 0) > 0;

  // Find best performers
  const topRecipe = dashboard?.top_recipes?.[0];
  const topNiche = dashboard?.top_niches?.[0];

  // Find opportunities - niches with listings but no sales
  const nichesWithoutSales =
    nichesData?.niches?.filter((n) => n.listing_count > 0 && n.total_orders === 0) || [];

  // Calculate conversion rate
  const conversionRate =
    dashboard?.overall?.pod_listings && dashboard.overall.pod_listings > 0
      ? ((dashboard.overall.listings_with_orders / dashboard.overall.pod_listings) * 100).toFixed(1)
      : '0';

  if (isLoadingAll) {
    return <AnalyticsSkeleton />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Analytics</h1>
          <p className="text-gray-500 mt-1">See what's working and where to focus next</p>
        </div>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="btn btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={clsx('w-4 h-4', syncMutation.isPending && 'animate-spin')} />
          {syncMutation.isPending ? 'Syncing...' : 'Sync Orders'}
        </button>
      </div>

      {/* Empty State - No Listings */}
      {!hasListings && !isLoading && (
        <div className="card bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-etsy-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Rocket className="w-8 h-8 text-etsy-orange" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ready to launch your first product?
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Start by researching trending niches, then generate designs that sell.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/research" className="btn btn-primary flex items-center gap-2">
                <Search className="w-4 h-4" />
                Find Trending Niches
              </Link>
              <Link to="/batch" className="btn btn-secondary flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Batch Generate
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Has Listings but No Sales */}
      {hasListings && !hasData && !isLoading && (
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-start gap-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                You have {dashboard?.overall?.pod_listings} listings live!
              </h2>
              <p className="text-gray-600 mb-4">
                No sales yet? Click "Sync Orders" to pull the latest data from Printify, or expand
                your catalog to increase chances of sales.
              </p>
              <div className="flex items-center gap-3">
                <Link to="/batch" className="btn btn-primary flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create More Listings
                </Link>
                <Link
                  to="/research"
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  Explore new niches
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid - Only show if has data */}
      {hasData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-700">Total Revenue</p>
                <p className="text-3xl font-bold text-green-900">
                  {formatCurrency(dashboard?.overall?.total_revenue_cents ?? 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500">
                <ShoppingCart className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold">{dashboard?.overall?.total_orders ?? 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">POD Listings</p>
                <p className="text-2xl font-bold">{dashboard?.overall?.pod_listings ?? 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-500">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Conversion Rate</p>
                <p className="text-2xl font-bold">{conversionRate}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Cards Grid */}
      {hasData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Recipe - Double Down */}
          {topRecipe && topRecipe.total_orders > 0 && (
            <div className="card border-2 border-green-200 bg-green-50/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                      Top Performer
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{topRecipe.recipe_name}</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    <span className="font-semibold text-green-600">
                      {formatCurrency(topRecipe.total_revenue_cents)}
                    </span>{' '}
                    from {topRecipe.total_orders} orders across {topRecipe.listing_count} listings
                  </p>
                  <Link
                    to={`/batch?recipe_id=${topRecipe.recipe_id}`}
                    className="inline-flex items-center gap-2 text-green-700 hover:text-green-900 font-semibold"
                  >
                    <Zap className="w-4 h-4" />
                    Create more with this recipe
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Top Niche - Double Down */}
          {topNiche && topNiche.total_orders > 0 && (
            <div className="card border-2 border-blue-200 bg-blue-50/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                      Best Niche
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{topNiche.niche_keyword}</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    <span className="font-semibold text-blue-600">
                      {formatCurrency(topNiche.total_revenue_cents)}
                    </span>{' '}
                    from {topNiche.total_orders} orders • {topNiche.product_type || 'POD'}
                  </p>
                  <Link
                    to={`/batch?niche_keyword=${encodeURIComponent(topNiche.niche_keyword)}`}
                    className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                    Add more designs to this niche
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Opportunities Section */}
      {nichesWithoutSales.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Opportunities</h2>
              <p className="text-sm text-gray-500">
                Niches with listings but no sales yet - consider adding more designs or trying new
                styles
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nichesWithoutSales.slice(0, 6).map((niche) => (
              <div
                key={niche.niche_id}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-etsy-orange transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{niche.niche_keyword}</span>
                  <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">
                    {niche.listing_count} listings
                  </span>
                </div>
                <Link
                  to={`/batch?niche_keyword=${encodeURIComponent(niche.niche_keyword)}`}
                  className="text-sm text-etsy-orange hover:text-orange-700 font-medium flex items-center gap-1"
                >
                  Try new styles
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Sales */}
      {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Sales</h2>
            <Link
              to="/inventory?status=published"
              className="text-sm text-etsy-orange hover:text-orange-700 font-medium"
            >
              View all listings
            </Link>
          </div>
          <div className="space-y-3">
            {dashboard.recent_activity.slice(0, 5).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/listing/${item.id}`}
                    className="font-medium text-gray-900 hover:text-etsy-orange truncate block"
                  >
                    {item.title}
                  </Link>
                  <p className="text-sm text-gray-500">
                    {item.niche_keyword || 'No niche'} • Last order {formatDate(item.last_order_at)}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <p className="font-bold text-green-600">{formatCurrency(item.revenue_cents)}</p>
                  <p className="text-sm text-gray-500">{item.orders} orders</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      {hasData && (
        <div className="card bg-gradient-to-r from-etsy-orange to-orange-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold mb-1">Ready to scale?</h2>
              <p className="text-orange-100">
                Use batch generation to create multiple designs at once based on your winning
                formulas.
              </p>
            </div>
            <Link
              to="/batch"
              className="btn bg-white text-etsy-orange hover:bg-orange-50 flex items-center gap-2 flex-shrink-0"
            >
              <Rocket className="w-4 h-4" />
              Batch Generate
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
